import { useReducer, useEffect, useCallback, useMemo } from "react";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { TaggedRow, TagPickerProps } from "./types";
import { tagReducer, initialTagState } from "./tagReducer";
import { calculateHasChanges, tagSortComparator } from "./tagUtils";
import { MAX_RECENT_TAGS } from "./tagUtils";

export const useRecentTags = (localStorageKey: string) => {
  const [recentTags, setRecentTags] = useLocalStorageState<string[]>(
    localStorageKey,
    { defaultValue: [] }
  );

  const addRecentTag = useCallback(
    (tag: string) => {
      setRecentTags((current) => {
        const updated = [tag, ...current.filter((t) => t !== tag)].slice(
          0,
          MAX_RECENT_TAGS
        );
        return updated;
      });
    },
    [setRecentTags]
  );

  return { recentTags, addRecentTag };
};

export const useTagState = <T extends TaggedRow>(
  selectedRows: T[],
  tagConfig: {
    entityName: string;
    localStorageKey: string;
    commonTags: string[];
    TagPickerComponent: React.ComponentType<TagPickerProps>;
    getEntityId: (row: T) => string;
  }
) => {
  const { recentTags } = useRecentTags(tagConfig.localStorageKey);

  const [state, dispatch] = useReducer(tagReducer, initialTagState);

  // Initialize when selectedRows or recentTags change
  useEffect(() => {
    dispatch({
      type: "INITIALIZE",
      payload: { selectedRows, recentTags },
    });
  }, [selectedRows, recentTags]);

  const hasChanges = useMemo(
    () => calculateHasChanges(state.selectedTags, state.initialSnapshot),
    [state.selectedTags, state.initialSnapshot]
  );

  const sortedTags = useMemo(
    () => state.selectedTags.sort(tagSortComparator),
    [state.selectedTags]
  );

  // Tag operation handlers
  const handleAddTag = useCallback(
    (tag: string) => {
      dispatch({ type: "ADD_TAG", payload: tag });
    },
    [dispatch]
  );

  const handleRemoveTag = useCallback(
    (tagName: string) => {
      dispatch({ type: "REMOVE_TAG", payload: tagName });
    },
    [dispatch]
  );

  const handleRestoreTag = useCallback(
    (tagName: string) => {
      dispatch({ type: "RESTORE_TAG", payload: tagName });
    },
    [dispatch]
  );

  const handlePromoteTag = useCallback(
    (tagName: string) => {
      dispatch({ type: "PROMOTE_TAG", payload: tagName });
    },
    [dispatch]
  );

  const handleRemoveAllTags = useCallback(() => {
    dispatch({ type: "REMOVE_ALL_TAGS" });
  }, [dispatch]);

  return {
    state,
    dispatch,
    hasChanges,
    sortedTags,
    handleAddTag,
    handleRemoveTag,
    handleRestoreTag,
    handlePromoteTag,
    handleRemoveAllTags,
  };
};
